import React from 'react';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

interface BadgeProps extends React.HTMLAttributes<HTMLDivElement> {
    variant?: 'primary' | 'gold' | 'neutral' | 'success' | 'warning' | 'danger' | 'dark' | 'outline';
    size?: 'sm' | 'md';
    shape?: 'pill' | 'rounded'; // pill = status, rounded = attribute
    children: React.ReactNode;
}

export function Badge({
    variant = 'neutral',
    size = 'md',
    shape = 'rounded',
    className,
    children,
    ...props
}: BadgeProps) {
    const variants = {
        primary: 'bg-primary-50 text-primary-700 border-primary-100', // Corporate Red Tint
        gold: 'bg-gold-50 text-gold-700 border-gold-100',             // Corporate Gold Tint
        neutral: 'bg-gray-100 text-gray-600 border-gray-200',
        success: 'bg-green-50 text-green-700 border-green-200',
        warning: 'bg-amber-50 text-amber-700 border-amber-200',       // Pending
        danger: 'bg-red-50 text-red-700 border-red-200',              // Cancelled
        dark: 'bg-charcoal text-white border-charcoal',               // High contrast
        outline: 'bg-transparent border-gray-300 text-gray-600'
    };

    const sizes = {
        sm: 'text-[10px] px-1.5 py-0.5 leading-none',
        md: 'text-xs px-2.5 py-1 leading-none'
    };

    const shapes = {
        pill: 'rounded-full',
        rounded: 'rounded-md'
    };

    return (
        <div
            className={cn(
                'inline-flex items-center justify-center font-bold border font-sans', // Open Sans enforced
                variants[variant],
                sizes[size],
                shapes[shape],
                className
            )}
            {...props}
        >
            {children}
        </div>
    );
}
